import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../store/store';
import {
    Box,
    Paper,
    Button,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import SearchIcon from '@mui/icons-material/Search';
import { format, subMonths } from 'date-fns';
import { leadConversionStyles } from './LeadConversionReport.styles';
import { fetchLeadConversionReport, setStartDate as setStoreStartDate, setEndDate as setStoreEndDate } from '../../store/slices/leadConversionReportSlice';



const LeadConversionReport: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const startDate = useSelector((state: RootState) => state.leadConversionReport.startDate);
    const endDate = useSelector((state: RootState) => state.leadConversionReport.endDate);
    const leadConversionData = useSelector((state: RootState) => state.leadConversionReport.data);
    const error = useSelector((state: RootState) => state.leadConversionReport.error);
    const loading = useSelector((state: RootState) => state.leadConversionReport.loading);

    useEffect(() => {
        if (startDate && endDate) {
            dispatch(fetchLeadConversionReport({
                startDate: format(startDate, "yyyy-MM-dd'T'00:00:00"),
                endDate: format(endDate, "yyyy-MM-dd'T'23:59:59"),
            }));
        }
    }, []);

    const handleStartDateChange = (date: Date | null) => {
        dispatch(setStoreStartDate(date));
    };

    const handleEndDateChange = (date: Date | null) => {
        dispatch(setStoreEndDate(date));
    };

    const handleSearch = () => {
        if (startDate && endDate) {
            dispatch(fetchLeadConversionReport({
                startDate: format(startDate, "yyyy-MM-dd'T'00:00:00"),
                endDate: format(endDate, "yyyy-MM-dd'T'23:59:59"),
            }));
        }
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box
                sx={leadConversionStyles.mainContainer}
            >
                <Paper sx={leadConversionStyles.filterPaper}>
                    <Box sx={leadConversionStyles.filterContainer}>

                        {/* Start Date */}
                        <Box sx={leadConversionStyles.filterBox}>
                            <DatePicker
                                label="Start Date"
                                format="dd/MM/yyyy"
                                value={startDate}
                                onChange={handleStartDateChange}
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        fullWidth: true,
                                        sx: leadConversionStyles.datePicker,
                                    },
                                }}
                            />
                        </Box>

                        {/* End Date */}
                        <Box sx={leadConversionStyles.filterBox}>
                            <DatePicker
                                label="End Date"
                                format="dd/MM/yyyy"
                                value={endDate}
                                onChange={handleEndDateChange}
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        fullWidth: true,
                                        sx: leadConversionStyles.datePicker,
                                    },
                                }}
                            />
                        </Box>
                        <Box sx={leadConversionStyles.searchButtonBox}>
                            <Button
                                variant="contained"
                                startIcon={<SearchIcon />}
                                fullWidth
                                sx={leadConversionStyles.searchButton}
                                onClick={handleSearch}
                            >Search</Button>
                        </Box>
                    </Box>
                </Paper>
                <Paper
                    sx={leadConversionStyles.tableContainer}>
                    <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow sx={leadConversionStyles.tableRow}>
                                    <TableCell sx={leadConversionStyles.tableCell}>Lead Source</TableCell>
                                    <TableCell sx={leadConversionStyles.tableCell}>Lead Arrived</TableCell>
                                    <TableCell sx={leadConversionStyles.tableCell}>Lead Hot</TableCell>
                                    <TableCell sx={leadConversionStyles.tableCell}>Lead Converted</TableCell>
                                    <TableCell sx={leadConversionStyles.tableCell}>Lead Lost</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                            <CircularProgress />
                                        </TableCell>
                                    </TableRow>
                                ) : error ? (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center" sx={{ py: 4, color: '#f44336' }}>
                                            {error}
                                        </TableCell>
                                    </TableRow>
                                ) : Object.keys(leadConversionData).length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center" sx={{ py: 4, color: '#999' }}>
                                            No data available
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    Object.entries(leadConversionData).map(([leadSource, data]) => (
                                        <TableRow key={leadSource} sx={leadConversionStyles.tableRow}>
                                            <TableCell sx={leadConversionStyles.tableCell}>{leadSource}</TableCell>
                                            <TableCell sx={leadConversionStyles.tableCell}>{data.leadArrived}</TableCell>
                                            <TableCell sx={leadConversionStyles.tableCell}>{data.leadHot}</TableCell>
                                            <TableCell sx={leadConversionStyles.tableCell}>{data.leadConverted}</TableCell>
                                            <TableCell sx={leadConversionStyles.tableCell}>{data.leadLost}</TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Box>
        </LocalizationProvider>
    );
};

export default LeadConversionReport;